import {createContext, useEffect, useState} from "react";
import {config} from "@/config.js";

/**
 * @typedef {Object} ThemeColors
 * @property {string} [--brand-logo]
 * @property {string} [--bg-app]
 * @property {string} [--bs-primary]
 * @property {string} [--bs-primary-rgb]
 * @property {string} [--bs-secondary]
 * @property {string} [--bs-secondary-rgb]
 * @property {string} [--bs-icon]
 * @property {string} [--bs-mix-weight]
 * @property {string} [--font-color]
 * @property {string} [--initialContainer-bg-color]
 * @property {string} [--bg-header]
 * @property {string} [--font-color-header]
 * @property {string} [--bs-header-result-doughnut]
 * @property {string} [--bg-body]
 * @property {string} [--bs-body-bg]
 * @property {string} [--input-bg]
 * @property {string} [--bs-font-accent-color]
 * @property {string} [--tabs-font-color-active]
 * @property {string} [--nav-tabs-link-active-border-color]
 * @property {string} [--tabs-border-color]
 * @property {string} [--title-color]
 */

/**
 * @typedef {Object} ThemeAssets
 * @property {string} [favicon]
 * @property {string} [favicon32]
 * @property {string} [favicon16]
 * @property {string} [appleTouchIcon]
 * @property {string} [msapplicationImage]
 * @property {string} [sidebarLogoPath]
 * @property {string} [mobileHeaderLogoPath]
 * @property {string} [themeColor]
 */

/**
 * @typedef {Object} NavItem
 * @property {string} path
 * @property {string} icon
 * @property {string} activeIcon
 * @property {string} name
 * @property {string} className
 * @property {boolean} show
 * @property {boolean} showForDemo
 */

/**
 * @typedef {Object} AccountMenuNavItem
 * @property {string} path
 * @property {string} title
 * @property {string} icon
 * @property {string} description
 * @property {boolean} show
 * @property {{path: string, title: string, show: boolean}[]} [subRoutes]
 */

/**
 * @typedef {Object} ThemeSettings
 * @property {boolean} [useWiderContainer]
 * @property {boolean} [showShopNavBtn]
 * @property {boolean} [showLearnNavBtn]
 * @property {boolean} [showThirdPartyAccess]
 * @property {boolean} [showWhatsNew]
 */

/**
 * @typedef {Object} ThemeConfig
 * @property {string} [appName]
 * @property {ThemeColors | Object.<string, string>} [colors]
 * @property {ThemeAssets} [assets]
 * @property {Object.<string, string>} [links]
 * @property {Object.<string, string>} [text]
 * @property {NavItem[]} [mobileNav]
 * @property {NavItem[]} [desktopNav]
 * @property {AccountMenuNavItem[]} [accountMenuNav]
 * @property {ThemeSettings} [settings]
 */

/**
 * @typedef {Object} ThemeContextValue
 * @property {'forth' | 'edge' | 'bt' | 'custom'} theme
 * @property {ThemeConfig} themeConfig
 * @property {'light' | 'dark'} variant
 * @property {(forceTheme?: string) => void} toggleTheme
 * @property {() => void} toggleVariant
 */

/** @type {import('react').Context<ThemeContextValue | undefined>} */

export const ThemeContext = createContext("");
const isForth = config.websiteId === 1;
const isEdge = config.websiteId === 2;
const isBiomarkerTracking = !isForth && !isEdge;

export const ThemeProvider = ({children}) => {
  const useCustomTheme = window.config.isWhiteLabelApp || false;
  const themeDefault = isForth ? "forth" : isEdge ? "edge" : useCustomTheme ? "custom" : "bt";
  const variantDefault = isForth || isBiomarkerTracking || useCustomTheme ? "light" : "dark";

  const [themeStore, variantStore] = localStorage.getItem("theme")?.split("-") || [themeDefault, variantDefault];
  const [themeConfig, setThemeConfig] = useState({});
  const [theme, setTheme] = useState(themeStore);
  const [variant, setVariant] = useState(variantStore);

  useEffect(() => {
    if (theme && variant) {
      localStorage.setItem("theme", `${theme}-${variant}`);
      document.documentElement.setAttribute("data-theme", theme);
      document.documentElement.setAttribute("data-variant", variant);
      document.documentElement.setAttribute("data-bs-theme", variant);
    }

    if (["forth", "edge", "bt"].includes(theme)) {
      handleBrandAssets(theme);
    }

    if (theme === "custom" && useCustomTheme) {
      handleCustomTheme();
    }
  }, [theme, variant, useCustomTheme]);

  const handleCustomTheme = async () => {
    setTheme("custom");
    setVariant("light");
    document.documentElement.setAttribute("data-bs-theme", "light");

    const response = await loadTheme(window.location.hostname);
    setThemeConfig(response || {});

    if (response.colors) {
      Object.entries(response.colors).forEach(([key, value]) => {
        document.documentElement.style.setProperty(`${key}`, value);
      });
    }

    handleBrandAssets("custom", response);
  };

  const toggleVariant = () => {
    const newVariant = variant == "dark" ? "light" : "dark";
    setVariant(newVariant);

    document.documentElement.setAttribute("data-variant", newVariant);
    document.documentElement.setAttribute("data-bs-theme", newVariant);
  };

  const toggleTheme = forceTheme => {
    let newTheme = theme === "forth" ? "edge" : "forth";
    let newVariant = theme === "forth" ? "dark" : "light";

    if (forceTheme) {
      newTheme = forceTheme;
      newVariant = forceTheme === "edge" ? "dark" : "light";
    }

    if (newTheme !== "custom") {
      window.config.isWhiteLabelApp = false;
    }

    setTheme(newTheme);

    document.documentElement.setAttribute("data-theme", newTheme);
    handleBrandAssets(newTheme);

    // Temporally
    setVariant(newVariant);
    document.documentElement.setAttribute("data-variant", newVariant);
    document.documentElement.setAttribute("data-bs-theme", newVariant);
  };

  const handleBrandAssets = (theme, themeConfig = {}) => {
    const favicon32 = document.getElementById("favicon32");
    const favicon16 = document.getElementById("favicon16");
    const faviconSvg = document.getElementById("faviconSvg");
    const favicon = document.getElementById("favicon");
    const appleTouchIcon = document.getElementById("apple-touch-icon");
    const msapplicationImage = document.getElementById("msapplication-image");
    const themeColor = document.getElementById("meta-theme-color");
    const metaDescription = document.head.querySelector("meta[name='description']");

    try {
      if (theme == "forth" || theme == "bt") {
        favicon32.href = "/favicon/favicon-32x32.png";
        favicon16.href = "/favicon/favicon-16x16.png";
        favicon.href = "/favicon/favicon.icon";
        appleTouchIcon.href = "/icons/apple-touch-icon.png";
        msapplicationImage.href = "/icons/android-chrome-192x192.png";
        themeColor.content = "#ffffff";
      }

      if (theme == "edge") {
        faviconSvg?.remove();
        favicon32.href = "/favicon/edge-favicon-32x32.png";
        favicon16.href = "/favicon/edge-favicon-16x16.png";
        favicon.href = "/favicon/edge-favicon.icon";
        appleTouchIcon.href = "/icons/edge-apple-touch-icon.png";
        msapplicationImage.href = "/icons/edge-android-chrome-192x192.png";
        themeColor.content = "#272c30";
        metaDescription.content =
          "Home blood testing designed for athletes Created by our team of sports-science experts, our sports specific blood tests are designed to optimise endurance, power and strength. Forth Edge Sports Blood Tests Our blood test kits are designed to give a range of athletes all the key information they need to understand how their bodies work…";
      }

      if (theme == "custom") {
        if (themeConfig?.assets?.favicon && favicon32) favicon32.href = themeConfig?.assets?.favicon;
        if (themeConfig?.assets?.favicon && favicon16) favicon16.href = themeConfig?.assets?.favicon;
        if (themeConfig?.assets?.favicon && favicon) favicon.href = themeConfig?.assets?.favicon;
        if (themeConfig?.assets?.favicon && faviconSvg) faviconSvg.href = themeConfig?.assets?.favicon;
        if (themeConfig?.assets?.appleTouchIcon && appleTouchIcon)
          appleTouchIcon.href = themeConfig?.assets?.appleTouchIcon;
        if (themeConfig?.assets?.msapplicationImage && msapplicationImage)
          msapplicationImage.href = themeConfig?.assets?.msapplicationImage;
        if (themeConfig?.assets?.themeColor && themeColor) themeColor.content = themeConfig?.assets?.themeColor;
        if (themeConfig?.texts?.metaDescription && metaDescription)
          metaDescription.content = themeConfig?.texts.metaDescription;
      }
    } catch (e) {
      console.log(e);
    }
  };

  const loadTheme = async (theme = DEFAULT_THEME) => {
    const themeUrl = `/white-label-themes/${theme}.json`;

    try {
      const response = await fetch(themeUrl, {headers: {"Content-Type": "application/json"}});

      if (!response.ok) {
        throw new Error(`Theme not found: ${theme}`);
      }

      const json = await response.json();
      return json;
    } catch (error) {
      console.error("Error loading theme: ", error);
    }
  };

  return (
    <ThemeContext.Provider value={{theme, themeConfig, variant, toggleTheme, toggleVariant}}>
      {children}
    </ThemeContext.Provider>
  );
};
